import { subscribeWithSelector } from 'zustand/middleware';
import { create } from 'zustand';
import { produce } from 'immer';

import { FIVE_SECONDS, showToast, TEN_SECONDS, TOAST_ERROR } from 'lib/utils';
import { PostFlaggedTransactionComment } from 'domain/usecases';
import { makeRemotePostQueueComment } from 'main/factories/usecases';
import { ApiStatusToUIStatusMapType, UIFlaggedTransaction, UIFlaggedTransactionStatus } from 'types/askcustomer';
import { AskRequest, AskRequestStatus, FlaggedTransaction } from '@hone-automation/common';
import { findIndex } from 'lodash';

interface FlaggedTransactionsState {
  activeFlaggedCell: AskRequest | undefined;
  activeFlaggedTransaction: AskRequest | undefined;
  transactionBeingFlagged: any | undefined;
  flaggedTransactions: AskRequest[];
  animatedTransaction: AskRequestStatus | undefined;
  setAnimatedTransaction: (status: AskRequestStatus | undefined) => void;
  addComment: (props: CreateCommentProps) => void;
  loadingFlaggedTransactions: boolean;
  setActiveTransaction: (trx: AskRequest | undefined) => void;
  setTransactionBeingFlagged: (trx: AskRequest | undefined) => void;
  addSingleFlaggedTransaction: (value: AskRequest) => void;
}

const ApiStatusToUIStatusMap: ApiStatusToUIStatusMapType = {
  Open: UIFlaggedTransactionStatus.INFORMATION,
  'In Progress': UIFlaggedTransactionStatus.SUBMITTED,
  Closed: UIFlaggedTransactionStatus.CATEGORIZED,
};

export function transformApiTrxToUITrx(apiTrx: AskRequest): AskRequest {
  return {
    ...apiTrx,
    status: ApiStatusToUIStatusMap[apiTrx.status] as any,
    updatedAt: new Date(apiTrx.updatedAt),
  };
}

type CreateCommentProps = PostFlaggedTransactionComment.Body & PostFlaggedTransactionComment.Params;

export const useFlaggedTransactions = create(
  subscribeWithSelector<FlaggedTransactionsState>(set => ({
    flaggedTransactions: [],
    activeFlaggedCell: undefined,
    loadingFlaggedTransactions: false,
    activeFlaggedTransaction: undefined,
    transactionBeingFlagged: undefined,
    animatedTransaction: undefined,
    setAnimatedTransaction: (status: AskRequestStatus | undefined) => {
      set({ animatedTransaction: status });
    },
    addSingleFlaggedTransaction: (value: AskRequest) => {
      set((state: FlaggedTransactionsState): FlaggedTransactionsState => {
        state.flaggedTransactions.push(value);
        state.transactionBeingFlagged!.flagged = value;
        return state;
      });
    },
    addComment: async ({ userId, trxId, comment, locationId }: CreateCommentProps) => {
      const service = makeRemotePostQueueComment(trxId);
      try {
        const response = (await service.post({ userId, comment, locationId })) as AskRequest;

        set(state => {
          const flaggedTransactionIdx = findIndex(state.flaggedTransactions, trx => trx.id === response.id);
          const UIFlaggedTrx = transformApiTrxToUITrx(response);
          return {
            activeFlaggedTransaction: UIFlaggedTrx,
            flaggedTransactions: produce(state.flaggedTransactions, draft => {
              if (draft) {
                draft[flaggedTransactionIdx] = UIFlaggedTrx;
              }
            }),
          };
        });
      } catch (e) {
        showToast('An unexpected error occurred while sending your message', TOAST_ERROR, TEN_SECONDS);
      }
    },
    setActiveTransaction: (trx: AskRequest | undefined) => {
      set({
        activeFlaggedTransaction: trx,
      });
    },
    setTransactionBeingFlagged: (trx: AskRequest | undefined) => {
      set({
        transactionBeingFlagged: trx,
      });
    },
  }))
);
